import React from 'react';
import { Rocket, Zap, Shield, Cpu, Clock, CheckCircle2 } from 'lucide-react';

const Changelog = () => {
  const versions = [
    {
      version: 'V3.0',
      date: 'January 2026',
      title: 'Major Performance & Architecture Overhaul',
      highlights: [
        { icon: Zap, text: 'WebRTC Migration: 6-9x faster latency (300-500ms vs 2-3s)' },
        { icon: Cpu, text: 'Microservices Architecture for scalability' },
        { icon: Shield, text: 'gRPC Bidirectional Processing' },
        { icon: Clock, text: 'Real-time Usage Dashboard with Token/Time Tracking' }
      ],
      features: [
        '34 Built-in Automations (Browser, Files, Apps, System, Windows, Clipboard, Spotify)',
        'DTLS encryption for voice streaming',
        'Short-lived JWT authentication',
        'Row-Level Security (RLS) in database',
        'LemonSqueezy payment integration',
        'Enhanced error handling and logging'
      ],
      improvements: [
        'Voice recognition accuracy improved by 40%',
        'Reduced memory footprint by 25%',
        'Faster app startup time (2s → 0.8s)',
        'Better Windows 11 integration'
      ]
    },
    {
      version: 'V2.5',
      date: 'December 2025',
      title: 'Stability & User Experience',
      features: [
        'HTTP/2 streaming (legacy)',
        'Basic automation library (18 commands)',
        'Simple usage tracking',
        'Manual authentication'
      ],
      metrics: {
        latency: '2-3 seconds',
        automations: '18',
        concurrent: '1 session'
      }
    }
  ];

  const comparison = [
    { metric: 'Latency', v25: '2-3 seconds', v30: '300-500ms', improvement: '6-9x faster' },
    { metric: 'Automations', v25: '18 commands', v30: '34 automations', improvement: '89% more' },
    { metric: 'Architecture', v25: 'Monolithic', v30: 'Microservices', improvement: 'Scalable' },
    { metric: 'Streaming', v25: 'HTTP/2', v30: 'WebRTC + gRPC', improvement: 'Real-time' },
    { metric: 'Concurrent Sessions', v25: '1', v30: 'Up to 10', improvement: '10x scale' },
    { metric: 'Security', v25: 'Basic TLS', v30: 'DTLS + RLS + JWT', improvement: 'Enterprise-grade' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-zinc-950 to-black pt-24 pb-20">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-orange-500/20 to-red-500/20 rounded-full mb-6">
            <Rocket className="w-10 h-10 text-orange-500" />
          </div>
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-4">
            Changelog
          </h1>
          <p className="text-xl text-gray-400">
            Track our journey to faster, more secure voice automation
          </p>
        </div>

        {/* V3.0 Details */}
        {versions.map((ver, idx) => (
          <div key={idx} className="mb-16">
            <div className="bg-gradient-to-br from-orange-500/10 to-red-500/10 border border-orange-500/30 rounded-2xl p-8 mb-8">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-4xl font-bold text-white mb-2">{ver.version}</h2>
                  <p className="text-gray-400">{ver.date}</p>
                </div>
                {idx === 0 && (
                  <span className="px-4 py-2 bg-green-500/20 text-green-500 rounded-full font-semibold text-sm border border-green-500/30">
                    Latest
                  </span>
                )}
              </div>
              <h3 className="text-2xl font-semibold text-white mb-6">{ver.title}</h3>
              
              {/* Highlights */}
              {ver.highlights && (
                <div className="grid md:grid-cols-2 gap-4 mb-8">
                  {ver.highlights.map((highlight, hidx) => {
                    const Icon = highlight.icon;
                    return (
                      <div key={hidx} className="flex items-start space-x-3 bg-black/40 rounded-lg p-4">
                        <Icon className="w-6 h-6 text-orange-500 flex-shrink-0 mt-1" />
                        <span className="text-gray-300">{highlight.text}</span>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Features */}
              {ver.features && (
                <div className="mb-6">
                  <h4 className="text-lg font-semibold text-white mb-4">New Features</h4>
                  <ul className="space-y-2">
                    {ver.features.map((feature, fidx) => (
                      <li key={fidx} className="flex items-start space-x-3">
                        <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-300">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Improvements */}
              {ver.improvements && (
                <div>
                  <h4 className="text-lg font-semibold text-white mb-4">Performance Improvements</h4>
                  <ul className="space-y-2">
                    {ver.improvements.map((imp, iidx) => (
                      <li key={iidx} className="flex items-start space-x-3">
                        <Zap className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-300">{imp}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        ))}

        {/* Comparison Table */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-white mb-8 text-center">V2.5 vs V3.0 Comparison</h2>
          <div className="bg-zinc-900/50 border border-white/5 rounded-xl overflow-hidden">
            <table className="w-full">
              <thead className="bg-black/40">
                <tr>
                  <th className="px-6 py-4 text-left text-white font-semibold">Metric</th>
                  <th className="px-6 py-4 text-left text-gray-400 font-semibold">V2.5</th>
                  <th className="px-6 py-4 text-left text-orange-500 font-semibold">V3.0</th>
                  <th className="px-6 py-4 text-left text-green-500 font-semibold">Improvement</th>
                </tr>
              </thead>
              <tbody>
                {comparison.map((row, idx) => (
                  <tr key={idx} className="border-t border-white/5 hover:bg-white/5 transition-colors">
                    <td className="px-6 py-4 text-white font-medium">{row.metric}</td>
                    <td className="px-6 py-4 text-gray-400">{row.v25}</td>
                    <td className="px-6 py-4 text-orange-400 font-semibold">{row.v30}</td>
                    <td className="px-6 py-4 text-green-400">{row.improvement}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* CTA */}
        <div className="bg-gradient-to-r from-orange-500/10 to-red-500/10 border border-orange-500/20 rounded-xl p-8 text-center">
          <h3 className="text-2xl font-bold text-white mb-4">
            Experience the V3.0 Speed
          </h3>
          <p className="text-gray-300 mb-6">
            Download now and feel the difference of 300-500ms latency
          </p>
          <a
            href="#download"
            className="inline-flex items-center space-x-2 px-8 py-3 bg-gradient-to-r from-orange-600 to-red-600 text-white font-semibold rounded-lg hover:shadow-xl hover:shadow-orange-500/50 transition-all duration-300 hover:scale-105"
          >
            <span>Download V3.0</span>
          </a>
        </div>
      </div>
    </div>
  );
};

export default Changelog;